import React, { useContext, useState } from "react";
import { InvoiceFilters } from "@common/contracts/invoice.contract";
import { pluralize } from "@common/helpers/string";
import { InvoicesByCustomerView as InvoicesByCustomerViewType } from "@common/types/views/invoiceViews";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { CommandIslandTotal } from "@design-system/CommandIsland/CommandIslandTotal";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { Pagination } from "@design-system/Pagination/Pagination";
import { HStack } from "@design-system/HStack/HStack";
import { InvoicesByCounterpartyTableContext } from "../../InvoicesByCounterpartyTable.context";
import { InvoicesDrillDownDrawer } from "../../InvoicesDrillDownDrawer";
import { useInvoiceTableEmptyView } from "../../useInvoiceTableEmptyView";
import { defaultInvoicesView } from "../defaultInvoiceViews";
import { InvoicesByCustomerView } from "./InvoicesByCustomerView";

interface InvoicesByCustomerViewContainerProps {
    view: InvoicesByCustomerViewType;
    hasConnectedInvoiceAccounts: boolean;
    onClearFilters: () => void;
}

export const InvoicesByCustomerViewContainer: React.FC<
    InvoicesByCustomerViewContainerProps
> = ({ view, hasConnectedInvoiceAccounts, onClearFilters }) => {
    const {
        data: invoicesByCounterparty = [],
        isLoading,
        currentSort,
        handleSortChange,
        totalPages,
        currentPage,
        setPage,
        counterpartiesCount,
    } = useContext(InvoicesByCounterpartyTableContext);

    const [drilldownParams, setDrilldownParams] = useState<InvoiceFilters>();

    const onDrilldown = (params: InvoiceFilters) => {
        setDrilldownParams(params);
    };

    const { emptyView, shouldHideFooter } = useInvoiceTableEmptyView({
        isLoading,
        filteredCount: counterpartiesCount,
        hasConnectedInvoiceAccounts,
        onClearFilters,
        viewKind: "customers",
    });

    const footer = (
        <CommandIsland
            sticky
            className={commandIslandStyles.bulkActionsStickyFooter}
        >
            <HStack gap={8}>
                {totalPages > 1 && (
                    <Pagination
                        size="md"
                        pageCount={totalPages}
                        currentPage={currentPage}
                        onPageChange={setPage}
                    />
                )}
                <CommandIslandTotal>
                    {counterpartiesCount}{" "}
                    {pluralize("customer", counterpartiesCount)}
                </CommandIslandTotal>
            </HStack>
        </CommandIsland>
    );

    return (
        <>
            <InvoicesByCustomerView
                view={view}
                invoicesByCustomer={invoicesByCounterparty}
                sort={currentSort}
                onSortChange={handleSortChange}
                onDrilldown={onDrilldown}
                footer={shouldHideFooter ? undefined : footer}
                emptyView={emptyView}
            />

            <InvoicesDrillDownDrawer
                view={defaultInvoicesView}
                open={!!drilldownParams}
                onClose={() => setDrilldownParams(undefined)}
                parentFilters={drilldownParams}
            />
        </>
    );
};
